/* Local persistence.

   The roster goes into IndexedDB so the desk keeps working if the venue wifi
   drops mid-shift. The print log is per-device by design: the ticket API is
   read-only, so there is nowhere shared to write it. Two desks each keep their
   own list — the "already printed" warning is a courtesy, not a lock. */

const DB_NAME = 'smdd2026';
const STORE = 'kv';
const LOG_KEY = 'smdd.checkins';   // original name kept: renaming orphans live logs

/* Bumped whenever a cached roster from an older build would be wrong. v1
   silently carried last year's DAY12025 tickets. v3 is the first whose rows can
   be trusted to be all of them -- v2 was fetched a page at a time, which the
   list endpoint serves unstably, and it also gave family their host's claim
   date, so a stale v2 roster reports a different total and files family in the
   wrong wave. */
const ROSTER_VERSION = 3;

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idb(mode, fn) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function saveRoster(roster) {
  try {
    await idb('readwrite', s => s.put({ ...roster, v: ROSTER_VERSION }, 'roster'));
  } catch { /* private browsing, quota — the app still works in memory */ }
}

export async function loadRoster(expectedSku) {
  try {
    const cached = await idb('readonly', s => s.get('roster'));
    if (!cached) return null;
    if (cached.v !== ROSTER_VERSION) return null;
    if (expectedSku && cached.sku !== expectedSku) return null;
    return cached;
  } catch { return null; }
}

export async function clearRoster() {
  try { await idb('readwrite', s => s.delete('roster')); } catch { /* noop */ }
}

/* ------------------------------------------------------- QR payload index

   The ticket API re-encrypts qr_string with a fresh nonce on every call, so the
   same ticket comes back as a different string each time. Anything we printed
   is therefore only recognisable if we still remember the exact string that
   went onto it — so every payload we have ever seen is kept, not just the ones
   from the current load. */

const MAX_PAYLOADS = 60000;

export async function loadPayloadIndex() {
  try { return (await idb('readonly', s => s.get('payloads'))) || {}; }
  catch { return {}; }
}

export async function rememberPayloads(attendees) {
  try {
    const index = await loadPayloadIndex();
    let added = 0;
    for (const a of attendees) {
      if (a.qr && a.ticket && index[a.qr] !== a.ticket) {
        index[a.qr] = a.ticket;
        added++;
      }
    }
    if (!added) return index;
    const keys = Object.keys(index);
    if (keys.length > MAX_PAYLOADS) {
      for (const k of keys.slice(0, keys.length - MAX_PAYLOADS)) delete index[k];
    }
    await idb('readwrite', s => s.put(index, 'payloads'));
    return index;
  } catch { return {}; }
}

/* ------------------------------------------------------------- print log

   This system prints badges; it does not check anybody in. Check-in has its own
   system, and a log that claimed otherwise would be read as an attendance
   record it has no standing to make. So the only question asked here is whether
   a badge has come off the printer yet.

   Rows are still written under the original storage key so a desk mid-shift
   does not lose its history, and rows written before the rename are read back
   through their old `issued` field. */

export function readLog() {
  try { return JSON.parse(localStorage.getItem(LOG_KEY) || '[]'); }
  catch { return []; }
}

export function appendLog(entry) {
  const rows = readLog();
  rows.push({ at: new Date().toISOString(), ...entry });
  // Keep the tail bounded so a long event can't fill the quota.
  localStorage.setItem(LOG_KEY, JSON.stringify(rows.slice(-5000)));
  return rows;
}

export function clearLog() { localStorage.removeItem(LOG_KEY); }

/* `issued` was the old field name for the same thing. Rows that recorded a
   lookup rather than a print are filtered out here: older builds wrote those,
   and the log is a record of printing. */
const wasPrinted = row => row.printed ?? row.issued ?? false;

/** One row per badge that went to the printer, oldest first. */
export const printEvents = () => readLog().filter(wasPrinted);

/** Every ticket that has had a badge printed, with when and how many times. */
export function printedTickets() {
  const seen = new Map();
  for (const row of readLog()) {
    if (!wasPrinted(row)) continue;
    const prev = seen.get(row.ticket);
    seen.set(row.ticket, {
      ...row,
      first: prev?.first || row.at,
      at: row.at,                       // most recent wins
      times: (prev?.times || 0) + 1,
    });
  }
  return seen;
}

export function logStats() {
  const printed = printEvents();
  return {
    printed: printed.length,
    unique: new Set(printed.map(r => r.ticket)).size,
  };
}

/* Rows are stamped in UTC so they sort and survive a timezone change, but a desk
   in Jakarta reading a UTC clock is reading one seven hours wrong. Every time
   shown to a person, on screen or in the export, goes through here. */
export function localStamp(iso) {
  const d = new Date(iso);
  if (Number.isNaN(+d)) return '';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
       + `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

export const localClock = iso => localStamp(iso).slice(11, 16);

/* Passed in from the layout rather than imported, to keep persistence free of
   anything to do with how a badge looks. */
let CATEGORY_LABEL = {};
export const setCategoryLabels = map => { CATEGORY_LABEL = map; };

const csvCell = v => `"${String(v ?? '').replace(/"/g, '""')}"`;

const csv = (head, rows) =>
  [head.join(','), ...rows.map(r => r.map(csvCell).join(','))].join('\n');

/* The guest list as a spreadsheet: whoever is on screen after the filters, not
   only the rows the table stops at. The attendance column carries the value the
   ticketing side gave, not this app's reading of it -- a family ticket shows
   its host's answer, which is the one that applies to it. */
export function attendeeCsv(list) {
  const head = ['ticket_number', 'name', 'company', 'position', 'pillar',
                'user_category', 'attendance'];
  return csv(head, list.map(a => [
    a.ticket, a.name, a.company, a.position, a.pillar,
    CATEGORY_LABEL[a.category] || a.rawCategory || '',
    a.attendanceRaw || '',
  ]));
}

/* What a print run contains, as a checklist: the same four things the badge
   itself carries, plus the category that decides which card stock it is on.
   No ticket number -- this is read against the printed cards, and a ticket
   number appears on none of them. */
export function runCsv(list) {
  const head = ['name', 'company', 'position', 'pillar', 'user_category'];
  return csv(head, list.map(a => [
    a.name, a.company, a.position, a.pillar,
    CATEGORY_LABEL[a.category] || a.rawCategory || '',
  ]));
}

/* A record of what was printed: one row per badge that went to the printer, in
   the order they were printed. A guest printed twice is two rows, because two
   badges exist. Columns carry the fields the QR itself encodes -- ticket, name,
   company, position -- plus what the badge is printed from.

   The roster is passed in because the log stores only what was on screen at the
   time; pillar and position come from the guest list. A printed ticket missing
   from the roster (printed on this device, list reloaded since) still appears,
   with the details the log did keep, rather than being dropped from its own
   record. */
export function printedCsv(attendees) {
  const byTicket = new Map(attendees.map(a => [a.ticket, a]));
  const head = ['printed_at', 'ticket', 'name', 'company', 'position',
                'pillar', 'category'];
  const rows = printEvents().map(r => {
    const a = byTicket.get(r.ticket) || {};
    return [localStamp(r.at), r.ticket, a.name ?? r.name, a.company ?? r.company,
            a.position, a.pillar, a.category ?? r.category];
  });
  return csv(head, rows);
}

/* --------------------------------------------------------- QR decryption key

   Kept apart from preferences so that "forget the key" is one unambiguous
   action. It never leaves the device and is never sent anywhere. */

const KEY_KEY = 'smdd.qrKey';

export const qrKey = () => localStorage.getItem(KEY_KEY) || '';
export const saveQrKey = k => localStorage.setItem(KEY_KEY, k);
export const clearQrKey = () => localStorage.removeItem(KEY_KEY);

/* ------------------------------------------------------------- preferences */

const PREF_KEY = 'smdd.prefs';

/* Bumped when a stored preference would now be wrong. v2 stopped defaulting to
   confirmed-only, which quietly hid two thirds of the guest list. v3 is the
   smaller card: a desk carrying the old 90 x 60 label would print stickers
   wider than the panel they go on, and a saved preference would have kept doing
   it silently. */
const PREFS_VERSION = 3;

const DEFAULTS = {
  v: PREFS_VERSION,
  status: '',
  sku: 'DAY12026',
  fields: ['company', 'pillar'],
  /* The sticker that drops into the card's white panel. The panel is 95 x 65.93
     mm on the artboard; on the smaller stock it measures about 80 mm across,
     and 65.93 scaled to that width is 55.5. 55 is used rather than 55.5 so the
     sticker can never overhang the panel -- the shortfall is under half a
     millimetre, onto white. */
  labelWidthMm: 80, labelHeightMm: 55, labelMarginMm: 0,
  // The full card, given by height; the width follows the artboard's shape.
  cardHeightMm: 109,
  labelIncludeQr: true, labelGuides: false,
  autoPrint: true, warnOnReprint: true, sound: true,
  splitByCategory: true, familyMode: 'with_parent',
  pageMode: 'single', cropMarks: true,
  // Only guests who answered the RSVP get a badge.
  printAttendance: ['day1', 'day2', 'both'],
  /* Which categories bring their family along. null means never chosen, and is
     not the same as [] -- the first roster switches every category that has
     family on, but an operator who turns them all off keeps them off. */
  familyByCat: null,
  familyOnly: false,
};

export function prefs() {
  try {
    const stored = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
    if (stored.v !== PREFS_VERSION) {
      // Keep the harmless choices, drop the ones this version redefines.
      delete stored.status;
      delete stored.labelWidthMm;
      delete stored.labelHeightMm;
      stored.v = PREFS_VERSION;
    }
    return { ...DEFAULTS, ...stored };
  } catch { return { ...DEFAULTS }; }
}

export function savePrefs(patch) {
  const next = { ...prefs(), ...patch };
  localStorage.setItem(PREF_KEY, JSON.stringify(next));
  return next;
}
